import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";

const NAV = [
  { label: "Data Products", to: "/services/data-products-engineering" },
  { label: "Research", to: "/services/research-analysis" },
  { label: "Storytelling", to: "/services/communication-storytelling" },
  { label: "Publishing", to: "/services/distribution-reach" },
  { label: "Fresh Data", to: "/services/fresh-data-updates" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-black/10"
          : "bg-white/60 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14 py-4 flex items-center justify-between gap-6">
        <a href="/" className="flex items-center gap-2.5 shrink-0" data-testid="site-logo">
          <img
            src="/brand/onlyfacts-logo.png"
            alt="OnlyFacts"
            className="h-7 md:h-8 w-auto select-none"
            draggable="false"
          />
          <span className="overline text-[#0052FF]">Studio</span>
        </a>

        <nav className="hidden lg:flex items-center gap-5 xl:gap-6">
          <span
            data-testid="nav-services-label"
            className="font-mono text-[0.65rem] uppercase tracking-[0.25em] text-[#6B7280] pr-2 border-r border-black/15 mr-1"
          >
            Services /
          </span>
          {NAV.map((n, i) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="group inline-flex items-baseline gap-1.5 text-sm font-medium text-[#0A0A0A] hover:text-[#0052FF] transition-colors whitespace-nowrap"
            >
              <span className="font-mono text-[0.6rem] text-[#6B7280] group-hover:text-[#0052FF] transition-colors">
                {String(i + 1).padStart(2, "0")}
              </span>
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3 shrink-0">
          <a
            href="https://onlyfacts.io"
            target="_blank"
            rel="noreferrer"
            data-testid="header-platform-link"
            className="text-sm font-medium link-ink"
          >
            Platform
          </a>
          <a
            href="/#contact"
            data-testid="header-cta-button"
            onClick={() => window.gtag?.('event', 'cta_click', { location: 'desktop-header' })}
            className="group bg-[#0A0A0A] text-white text-sm font-medium px-5 py-2.5 hover:bg-[#0052FF] transition-colors inline-flex items-center gap-1.5"
          >
            Start a project
            <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </a>
        </div>

        <button
          className="lg:hidden p-2"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-black/10 bg-white" data-testid="mobile-menu">
          <div className="px-6 py-4 flex flex-col gap-3">
            <p className="font-mono text-[0.65rem] uppercase tracking-[0.25em] text-[#6B7280] pt-1 pb-2">
              Services
            </p>
            {NAV.map((n, i) => (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => {
                  setOpen(false);
                }}
                className="flex items-baseline gap-3 text-base font-medium py-2 border-b border-black/5"
              >
                <span className="font-mono text-xs text-[#6B7280]">{String(i + 1).padStart(2, "0")}</span>
                {n.label}
              </Link>
            ))}
            <a
              href="/#contact"
              onClick={() => {
                setOpen(false);
                window.gtag?.('event', 'cta_click', { location: 'mobile-header' });
              }}
              className="bg-[#0A0A0A] text-white text-center font-medium py-3 mt-2"
            >
              Start a project
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
